import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { studentService } from '../../services/studentService';
import { lessonService } from '../../services/lessonService';
import { Book, Trophy, Clock, Target, PlayCircle, Lock, CheckCircle, Star } from 'lucide-react';
import LoadingSpinner from '../shared/LoadingSpinner';
import Navbar from '../shared/Navbar';

const StudentDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [dashboard, setDashboard] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Get student ID from user (assuming it's stored during login)
            const studentsResponse = await studentService.getStudents();
            const studentId = studentsResponse.students[0]?.id;

            if (!studentId) {
                setError('Student profile not found');
                return;
            }

            // Fetch dashboard and lessons
            const [dashboardData, lessonsData] = await Promise.all([
                studentService.getStudentDashboard(studentId),
                lessonService.getLessons()
            ]);

            setDashboard(dashboardData.student);
            setLessons(lessonsData.lessons);
        } catch (err) {
            setError(err.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    const getLessonIcon = (status) => {
        switch(status) {
            case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'in_progress': return <PlayCircle className="w-5 h-5 text-blue-500" />;
            case 'locked': return <Lock className="w-5 h-5 text-gray-400" />;
            default: return <Book className="w-5 h-5 text-gray-400" />;
        }
    };

    const handleLessonClick = (lesson) => {
        if (lesson.progress_status !== 'locked') {
            navigate(`/lessons/${lesson.id}`);
        }
    };

    if (loading) return <LoadingSpinner message="Loading your dashboard..." />;

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <p className="text-red-800">{error}</p>
                        <button
                            onClick={fetchDashboardData}
                            className="mt-4 btn-primary"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!dashboard) return null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-purple-900">
                                Welcome back, {dashboard.full_name}! 🎉
                            </h1>
                            <p className="text-gray-600 mt-2">{dashboard.level_name}</p>
                        </div>
                        <div className="flex gap-4">
                            <div className="bg-purple-100 rounded-xl p-4 text-center">
                                <Trophy className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-purple-900">
                                    {dashboard.badges_earned} Badges
                                </p>
                            </div>
                            <div className="bg-blue-100 rounded-xl p-4 text-center">
                                <Star className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-blue-900">
                                    {Math.round(dashboard.progress)}% Complete
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-6">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Current Level Progress</span>
                            <span className="text-purple-600 font-semibold">
                {dashboard.stats.lessons_completed}/{dashboard.stats.total_lessons} lessons
              </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all"
                                style={{ width: `${dashboard.progress}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                    <div className="card">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Average Score</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {Math.round(dashboard.stats.average_score)}%
                                </p>
                            </div>
                            <Target className="w-8 h-8 text-purple-400" />
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Time Spent</p>
                                <p className="text-2xl font-bold text-blue-600">
                                    {Math.floor(dashboard.stats.total_time_minutes / 60)}h {dashboard.stats.total_time_minutes % 60}m
                                </p>
                            </div>
                            <Clock className="w-8 h-8 text-blue-400" />
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Completed</p>
                                <p className="text-2xl font-bold text-green-600">
                                    {dashboard.stats.lessons_completed}
                                </p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-green-400" />
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Badges</p>
                                <p className="text-2xl font-bold text-yellow-600">
                                    {dashboard.badges_earned}
                                </p>
                            </div>
                            <Trophy className="w-8 h-8 text-yellow-400" />
                        </div>
                    </div>
                </div>

                {/* Lessons Grid */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h2 className="text-2xl font-bold mb-6">Your Lessons</h2>

                    {lessons.length === 0 ? (
                        <div className="text-center py-12">
                            <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No lessons available yet</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {lessons.map((lesson) => (
                                <button
                                    key={lesson.id}
                                    onClick={() => handleLessonClick(lesson)}
                                    disabled={lesson.progress_status === 'locked'}
                                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                                        lesson.progress_status === 'locked'
                                            ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                                            : lesson.progress_status === 'completed'
                                                ? 'bg-green-50 border-green-200 hover:shadow-lg'
                                                : lesson.progress_status === 'in_progress'
                                                    ? 'bg-blue-50 border-blue-200 hover:shadow-lg'
                                                    : 'bg-white border-gray-200 hover:border-purple-400 hover:shadow-lg'
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        {getLessonIcon(lesson.progress_status)}
                                        {lesson.is_free && (
                                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full font-semibold">
                        FREE
                      </span>
                                        )}
                                    </div>

                                    <h3 className="font-bold text-lg mb-2 line-clamp-2">
                                        {lesson.title}
                                    </h3>

                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                        {lesson.description}
                                    </p>

                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                        <span>{lesson.duration_minutes} mins</span>
                                        {lesson.progress_status === 'completed' && lesson.score && (
                                            <span className="text-green-600 font-semibold">
                        Score: {lesson.score}%
                      </span>
                                        )}
                                    </div>

                                    {lesson.progress_status === 'locked' && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <p className="text-xs text-gray-500">
                                                Complete previous lesson to unlock
                                            </p>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Call to Action for Subscription */}
                {lessons.some(l => !l.is_free && l.progress_status === 'locked') && (
                    <div className="mt-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg p-8 text-white text-center">
                        <h3 className="text-2xl font-bold mb-2">
                            Want to unlock all lessons? 🚀
                        </h3>
                        <p className="mb-6 text-purple-100">
                            Get full access to all STEM lessons and experiments
                        </p>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="bg-white text-purple-600 px-8 py-3 rounded-xl font-bold hover:bg-purple-50 transition-all"
                        >
                            View Plans
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentDashboard;